import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../AuthContext';
import FileUpload from '../../components/FileUpload';
import fileService from '../../services/fileService';
import { getEffectiveAccountId, addScopeChangeListener } from '../../services/authorizationService';
import { 
  Plus, 
  MagnifyingGlass, 
  Download, 
  Trash, 
  File,
  Image,
  X
} from '@phosphor-icons/react';

export default function Posters() {
  const { currentUser } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('posters');
  const [customCategory, setCustomCategory] = useState('');
  const [editingCategory, setEditingCategory] = useState('');
  const [isEditingCategory, setIsEditingCategory] = useState(false);
  const [userFiles, setUserFiles] = useState([]);
  const [loading, setLoading] = useState(true);


  const loadUserFiles = useCallback(async () => {
    try {
      setLoading(true);
      const effectiveAccountId = getEffectiveAccountId();
      if (!effectiveAccountId) {
        console.log('No effective account ID, cannot load files');
        setUserFiles([]);
        return;
      }
      
      const files = await fileService.listUserFiles(effectiveAccountId);
      setUserFiles(files);
    } catch (error) {
      console.error('Error loading user files:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load user's uploaded files
  useEffect(() => {
    if (currentUser?.uid) {
      loadUserFiles();
    }
  }, [currentUser, loadUserFiles]);

  // Listen for account scope changes
  useEffect(() => {
    const unsubscribe = addScopeChangeListener((scopeData) => {
      console.log('Account scope changed in Posters:', scopeData);
      // Reload files when scope changes
      if (scopeData.effectiveAccountId && currentUser) {
        loadUserFiles();
      }
    });

    return unsubscribe;
  }, [currentUser, loadUserFiles]);

  // Handle file upload
  const handleFileUpload = async (file, category) => {
    if (!currentUser?.uid) return;

    try {
      const effectiveAccountId = getEffectiveAccountId();
      if (!effectiveAccountId) {
        alert('No account scope set. Please select an account.');
        return;
      }

      // Use custom category if selected, otherwise use the passed category
      const finalCategory = uploadCategory === 'custom' ? customCategory : category;
      
      if (!finalCategory || finalCategory.trim() === '') {
        alert('Please select or enter a category for the file.');
        return;
      }

      const uploadedFile = await fileService.uploadFile(file, effectiveAccountId, finalCategory.trim().toLowerCase());
      
      // Add to local state
      setUserFiles(prev => [...prev, uploadedFile]);
      
      // Close modal and reset form
      setShowUploadModal(false);
      setUploadCategory('posters');
      setCustomCategory('');
    } catch (error) {
      console.error('Error uploading file:', error);
      alert(`Upload failed: ${error.message}`);
    }
  };

  // Handle file deletion
  const handleFileDelete = async (fileId, filePath) => {
    try {
      await fileService.deleteFile(fileId, filePath);
      setUserFiles(prev => prev.filter(file => file.id !== fileId));
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Failed to delete file');
    }
  };

  // Handle category update
  const handleCategoryUpdate = async (fileId, newCategory) => {
    try {
      await fileService.updateFileCategory(fileId, newCategory);
      
      // Update local state
      setUserFiles(prev => prev.map(file => 
        file.id === fileId ? { ...file, category: newCategory } : file
      ));
      
      // Update selected material if it's the same file
      if (selectedMaterial && selectedMaterial.id === fileId) {
        setSelectedMaterial(prev => ({ ...prev, category: newCategory }));
      }
      
      setIsEditingCategory(false);
      setEditingCategory('');
    } catch (error) {
      console.error('Error updating category:', error);
      alert('Failed to update category');
    }
  };

  // Handle file download
  const handleFileDownload = async (file) => {
    try {
      const link = document.createElement('a');
      link.href = file.url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file');
    }
  };

  // Filter files based on category and search
  const filteredFiles = userFiles.filter(file => {
    const matchesCategory = selectedCategory === 'all' || 
      file.category.toLowerCase() === selectedCategory;
    const matchesSearch = file.name.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  // Get file icon based on type
  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) {
      return <Image className="w-8 h-8 text-blue-500" />;
    }
    return <File className="w-8 h-8 text-gray-500" />;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format upload date
  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your materials...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Marketing Materials</h2>
          <p className="text-gray-600">Manage and organize your healthcare marketing content</p>
        </div>
        <button onClick={() => setShowUploadModal(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
          <Plus className="w-5 h-5" />
          <span>Upload Material</span>
        </button>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Category Filter */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="all">All Categories</option>
              <option value="posters">Posters</option>
              <option value="flyers">Flyers</option>
              <option value="ads">Ads</option>
              <option value="brochures">Brochures</option>
              <option value="social">Social Media</option>
            </select>
          </div>

          {/* Search */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <MagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input type="text" placeholder="Search materials..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Materials Grid */}
      <div className="bg-white rounded-lg shadow">
        {filteredFiles.length === 0 ? (
          <div className="text-center py-12">
            <File className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No materials found</h3>
            <p className="text-gray-500 mb-4">Upload your first marketing material to get started</p>
            <button onClick={() => setShowUploadModal(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
              Upload Material
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
            {filteredFiles.map((file) => (
              <div key={file.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                {/* Thumbnail or Icon */}
                <div className="mb-3">
                  {file.type.startsWith('image/') ? (
                    <div className="w-full h-32 bg-gray-100 rounded-lg overflow-hidden mb-3">
                      <img 
                        src={file.url} 
                        alt={file.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="w-full h-full flex items-center justify-center" style={{display: 'none'}}>
                        {getFileIcon(file.type)}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center w-full h-32 bg-gray-100 rounded-lg mb-3">
                      {getFileIcon(file.type)}
                    </div>
                  )}
                </div>
                
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 mb-2 truncate">{file.name}</h3>
                    <p className="text-sm text-gray-500 mb-3">{formatFileSize(file.size)} • {formatDate(file.uploadedAt)}</p>
                  </div>
                  <div className="flex space-x-2 ml-2">
                    <button onClick={() => handleFileDownload(file)} className="text-blue-600 hover:text-blue-800 p-1" title="Download"><Download className="w-4 h-4" /></button>
                    <button onClick={() => handleFileDelete(file.id, file.path)} className="text-red-600 hover:text-red-800 p-1" title="Delete"><Trash className="w-4 h-4" /></button>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{file.category}</span>
                  <button onClick={() => setSelectedMaterial(file)} className="text-blue-600 hover:text-blue-800 text-sm font-medium">View Details</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Upload Marketing Material</h3>
              <button onClick={() => setShowUploadModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Category Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <select 
                value={uploadCategory} 
                onChange={(e) => {
                  setUploadCategory(e.target.value);
                  if (e.target.value !== 'custom') {
                    setCustomCategory('');
                  }
                }} 
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="posters">Posters</option>
                <option value="flyers">Flyers</option>
                <option value="ads">Ads</option>
                <option value="brochures">Brochures</option>
                <option value="social">Social Media</option>
                <option value="custom">Custom Category</option>
              </select>
              
              {/* Custom Category Input */}
              {uploadCategory === 'custom' && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Custom Category Name</label>
                  <input
                    type="text"
                    value={customCategory}
                    onChange={(e) => setCustomCategory(e.target.value)}
                    placeholder="Enter custom category name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>
            
            <FileUpload onFileUpload={handleFileUpload} category={uploadCategory === 'custom' ? customCategory : uploadCategory} />
          </div>
        </div>
      )}

      {/* Material Details Modal */}
      {selectedMaterial && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Material Details</h3>
              <button onClick={() => {
                setSelectedMaterial(null);
                setIsEditingCategory(false);
                setEditingCategory('');
              }} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Image Preview */}
              <div className="space-y-4">
                {selectedMaterial.type.startsWith('image/') ? (
                  <div className="w-full">
                    <img 
                      src={selectedMaterial.url} 
                      alt={selectedMaterial.name}
                      className="w-full h-auto max-h-96 object-contain rounded-lg border border-gray-200"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-full h-48 flex items-center justify-center bg-gray-100 rounded-lg border border-gray-200" style={{display: 'none'}}>
                      {getFileIcon(selectedMaterial.type)}
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-48 flex items-center justify-center bg-gray-100 rounded-lg border border-gray-200">
                    {getFileIcon(selectedMaterial.type)}
                  </div>
                )}
              </div>
              
              {/* File Details */}
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  {getFileIcon(selectedMaterial.type)}
                  <div>
                    <h4 className="font-medium text-gray-900">{selectedMaterial.name}</h4>
                    <p className="text-sm text-gray-500">{formatFileSize(selectedMaterial.size)}</p>
                  </div>
                </div>
                
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Category:</span>
                    {isEditingCategory ? (
                      <div className="mt-2 flex items-center space-x-2">
                        <input
                          type="text"
                          value={editingCategory}
                          onChange={(e) => setEditingCategory(e.target.value)}
                          className="flex-1 px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter new category"
                        />
                        <button
                          onClick={() => handleCategoryUpdate(selectedMaterial.id, editingCategory)}
                          className="bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 text-xs"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingCategory(false);
                            setEditingCategory('');
                          }}
                          className="bg-gray-300 text-gray-700 px-3 py-1 rounded-md hover:bg-gray-400 text-xs"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="mt-1 flex items-center space-x-2">
                        <span className="text-gray-600">{selectedMaterial.category}</span>
                        <button
                          onClick={() => {
                            setEditingCategory(selectedMaterial.category);
                            setIsEditingCategory(true);
                          }}
                          className="text-blue-600 hover:text-blue-800 text-xs underline"
                        >
                          Edit
                        </button>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <span className="font-medium text-gray-700">Uploaded:</span>
                    <span className="ml-2 text-gray-600">{formatDate(selectedMaterial.uploadedAt)}</span>
                  </div>
                  
                  <div>
                    <span className="font-medium text-gray-700">Type:</span>
                    <span className="ml-2 text-gray-600">{selectedMaterial.type}</span>
                  </div>
                </div>
                
                <div className="flex space-x-3 pt-4">
                  <button onClick={() => handleFileDownload(selectedMaterial)} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
                    <Download className="w-4 h-4" />
                    <span>Download</span>
                  </button>
                  <button onClick={() => handleFileDelete(selectedMaterial.id, selectedMaterial.path)} className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2">
                    <Trash className="w-4 h-4" />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
